import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { UserApiService } from '../../shared/services/api/user-api.service';
import { TenantContextService } from '../tenant/tenant-context.service';
import { TenantLifecycleService } from '../tenant/tenant-lifecycle.service';
import {
  CONFIGURATION_NAV_FLAG_KEY,
  FeatureFlagService,
} from '../feature-flags/feature-flag.service';
import { DIVE_EMBED_MANAGE } from './dive-embed-permissions';
import { PRICE_BAND_RUN_MANAGE } from './price-band-run-permissions';
import { UI_CONFIG_MANAGE } from './ui-config-admin.guard';
import { USER_PERMISSIONS_MANAGE } from './user-permissions.constants';
import { USER_TYPES_MANAGE } from './user-types.constants';
import { PRICING_ACTION_ENABLE } from './pricing-action-permissions';
import { ROLES_MANAGE } from './capabilities';
import { TENANT_MEMBERSHIPS_MANAGE } from './tenant-memberships-admin.guard';

/** DB keys for the overview's tools; Configuration is gated by flag + `isPrivileged()` instead. */
export const ADMIN_TOOL_PERMISSION_KEYS: readonly string[] = [
  UI_CONFIG_MANAGE,
  'column_mapping.manage',
  TENANT_MEMBERSHIPS_MANAGE,
  ROLES_MANAGE,
  'data_scope.manage',
  USER_PERMISSIONS_MANAGE,
  USER_TYPES_MANAGE,
  PRICING_ACTION_ENABLE,
  'email_subscriptions.manage',
  DIVE_EMBED_MANAGE,
  PRICE_BAND_RUN_MANAGE,
];

/** Guards `/admin`: activates if the caller can reach at least one listed tool, else `/home`. */
export const adminOverviewGuard: CanActivateFn = async () => {
  const userApi = inject(UserApiService);
  const router = inject(Router);
  const flags = inject(FeatureFlagService);
  const lifecycle = inject(TenantLifecycleService);
  const tenantContext = inject(TenantContextService);

  try {
    await firstValueFrom(lifecycle.bootstrapTenantContext());
    await flags.ready();

    const me = await firstValueFrom(userApi.getUserData());
    const hasToolPermission = ADMIN_TOOL_PERMISSION_KEYS.some((key) =>
      me.permissions?.includes(key)
    );
    const hasConfiguration =
      flags.isEnabled(CONFIGURATION_NAV_FLAG_KEY) && tenantContext.isPrivileged();

    if (hasToolPermission || hasConfiguration) {
      return true;
    }
  } catch {
    // Network / auth / bootstrap issues fall through to redirect, same as
    // every individual admin guard.
  }
  return router.createUrlTree(['/home']);
};
